import * as _ from "lodash";
import {IBusinessObject, IBusinessObjectCreateLoadOptions} from "../PA.Core/PA.Core.IF.BO";
import {IMessageProvider, IProgressProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {
    WB_CompanyRow, WB_MasterDataSetRow,
    WB_PeriodHierarchyCompanyRelationshipRow,
    WB_PeriodHierarchyLevelRow,
    WB_PeriodHierarchyRow, WB_SourceRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbPeriodHierarchy} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Company, esWB_MasterData_Set,
    esWB_Period_Hierarchy, esWB_Period_Hierarchy_Company_Relationship,
    esWB_Period_Hierarchy_Level, esWB_Source
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {BehaviorSubject} from "rxjs";
import {PeriodHierarchyBOValidations} from "./PeriodHierarchyBO.Validation";

export class PeriodHierarchyBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_PeriodHierarchy: WB_PeriodHierarchyRow;

    constructor(options?: IBusinessObjectCreateLoadOptions) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New Period Hierarchy" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);
        this.drWB_PeriodHierarchy = null;
        this.IHaveCompanies_init()
    }

    get WB_PeriodHierarchy(): WB_PeriodHierarchyRow {
        return this.drWB_PeriodHierarchy;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: IBusinessObjectCreateLoadOptions): Promise<IBusinessObject> {
        var self = this;
        this.LoadProgressProvider.start("Creating Period Hierarchy");
        let internalOnComplete = self._OnLoadComplete = (async() => {
            try{
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Period Hierarchy.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);

                return self;
            }

            // Create record using dbContext
            let newPeriodhRow: WB_PeriodHierarchyRow=<WB_PeriodHierarchyRow> await self.dbContext.createRecordWithKey(esWB_Period_Hierarchy)

                    newPeriodhRow.setDefaults();
                    self.drWB_PeriodHierarchy = newPeriodhRow;
                    //add this to all the BOs
                    self.dbContext.add(esWB_Period_Hierarchy, newPeriodhRow);

                    self.Identity.setAll(
                        self.WB_PeriodHierarchy.cif.WB_PERIODH_ID,
                        self.WB_PeriodHierarchy.cif.WB_PERIOD_HIER_ID,
                        self.WB_PeriodHierarchy.cif.WB_PERIOD_HIER_NAME
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    );
                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                await self.applyCreateOptions()
                self.Identity.setAll(self.WB_PeriodHierarchy.cif.WB_PERIODH_ID, self.WB_PeriodHierarchy.cif.WB_PERIOD_HIER_ID, self.WB_PeriodHierarchy.cif.WB_PERIOD_HIER_NAME);
                    self.Identity.listenToRecordUpdates(
                        self.WB_PeriodHierarchy,
                        "WB_PERIODH_ID",
                        "WB_PERIOD_HIER_ID",
                        "WB_PERIOD_HIER_NAME"
                    );

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                  return self;
                } catch(err ) {
                    self.LoadProgressProvider.exception(err);
                    return self
                }})();


        return internalOnComplete;
    }

    async applyCreateOptions() {
        const self = this

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId
        if (!_.isEmpty(sId))
            self.WB_PeriodHierarchy.cif.WB_PERIOD_HIER_ID = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_PeriodHierarchy.cif.WB_PERIOD_HIER_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_PeriodHierarchy.WB_MasterDataSetRow = mds
                self.WB_PeriodHierarchy.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Source
        let wbSource = self.CreateOptions?.wbSourceId || self.LoadOptions?.wbSourceId
        if (_.isNumber(wbSource)) {
            let src: WB_SourceRow = <any>await self.dbContext.find(esWB_Source, wbSource)
            if (src) {
                self.WB_PeriodHierarchy.cif.SOURCE_ID = src.cif.SOURCE_ID
            }
        }

    }
    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        var self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        if (!this.bIsLoaded)
            this.Identity.setAll(anId, anId, "Loading Period Hierarchy (" + anId + ")");

        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

            if (self.bIsLoaded) {
                var sError = "Period Hierarchy already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Period Hierarchy.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }
            //Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = PeriodHierarchyBO.createRecordResolver({
                    dbContext: self.dbContext
                });
                recordResolverLoadPromise = PeriodHierarchyBO.setupRecordResolver(
                    recordResolver,
                    [anId]
                );
            }

            recordResolverLoadPromise
                .then(function() {
                    let aPeriodhRow: any = recordResolver.get(
                        esWB_Period_Hierarchy,
                        anId
                    );

                    if (_.isEmpty(aPeriodhRow)) {
                        reject("Period Hierarchy not Found");
                        return;
                    }

                    self.drWB_PeriodHierarchy = aPeriodhRow;
                    self.Identity.setAll(
                        self.WB_PeriodHierarchy.cif.WB_PERIODH_ID,
                        self.WB_PeriodHierarchy.cif.WB_PERIOD_HIER_ID,
                        self.WB_PeriodHierarchy.cif.WB_PERIOD_HIER_NAME
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_PeriodHierarchy,
                        "WB_PERIODH_ID",
                        "WB_PERIOD_HIER_ID",
                        "WB_PERIOD_HIER_NAME"
                    );
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch(function(err: any) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                });
        });

        return this._OnLoadComplete;
    }


    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            "Validating " +
            self.WB_PeriodHierarchy.cif.WB_PERIOD_HIER_NAME +
            "(" +
            self.WB_PeriodHierarchy.cif.WB_PERIOD_HIER_ID +
            ")"
        );
       try {
           // Does no validations yet
           self.ValidateOptions = options || {};

           if (self.dbContext == null) {
               var sError = "Unable to determine dbContext for to Validate Period Hierarchy.";
               self.ValidateProgressProvider.exception(sError);
               throw sError;
           }

           await PeriodHierarchyBOValidations.ValidateFields(self)
           if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
               self._bIsValidated = false;
           } else {
               self._bIsValidated = true;
           }
           self.ValidateProgressProvider.finish();

       }catch (err) {
               self._bIsValidated = false;
               self.ValidateProgressProvider.exception(err);

           }
           return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbPeriodh = <IwbPeriodHierarchy>self.WB_PeriodHierarchy;
        this.SaveProgressProvider.start(
            "Saving " +
            wbPeriodh.WB_PERIOD_HIER_NAME +
            "(" +
            wbPeriodh.WB_PERIOD_HIER_ID +
            ")"
        );

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (this.dbContext == null) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save Meta.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return;
            }
            self.SaveOptions = options || {};

            var saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any
            ) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            let tasks: Promise<any>[] = [];
            let bDone = false

            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return;
                    }
                }).then((ignore)=> {
                if (bDone) return
                return  saveFunction(esWB_Period_Hierarchy, self.drWB_PeriodHierarchy, {})
                .then(() => {
                    tasks.push(saveFunction(esWB_Period_Hierarchy_Level, self.WB_PeriodHierarchy.WB_PeriodHierarchyLevelRows));
                    tasks.push(saveFunction(esWB_Period_Hierarchy_Company_Relationship, self.WB_PeriodHierarchy.WB_PeriodHierarchyCompanyRelationshipRow));

                    return Promise.all(tasks);
                })
                .then(ignored => {

                    if (bDone) return;
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        });
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch(err => {
                    self.SaveProgressProvider.exception(err);
                    resolve(self);
                });
        })
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(
        bSaveNow: boolean,
        options?: any
    ): Promise<IBusinessObject> {
        var self = this;
        var wbPeriodh = <IwbPeriodHierarchy>self.drWB_PeriodHierarchy;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            "Deleting " + wbPeriodh.WB_PERIOD_HIER_NAME + "(" + wbPeriodh.WB_PERIOD_HIER_ID + ")"
        );
        return new Promise<IBusinessObject>((resolve, error) => {
            // 1. Validate if we can delete the Meta

            // 2. If Valid, mark all the records for deletion.
            self.drWB_PeriodHierarchy.markDeleted();

            _.forEach(self.drWB_PeriodHierarchy.WB_PeriodHierarchyCompanyRelationshipRow, (esc: WB_PeriodHierarchyCompanyRelationshipRow) => {
                if (esc.isActive) {
                    esc.markDeleted()
                }
            })
            _.forEach(self.drWB_PeriodHierarchy.WB_PeriodHierarchyLevelRows, (esc: WB_PeriodHierarchyLevelRow) => {
                if (esc.isActive) {
                    esc.markDeleted()
                }
            })
            if (bSaveNow) {
                self
                    .SaveUsing(false)
                    .then(ignore => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch(err => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    _companyChanged: BehaviorSubject<PeriodHierarchyBO>

    IHaveCompanies_init() {
        this._companyChanged = new BehaviorSubject<PeriodHierarchyBO>(this)
    }

    get CompanyChanged(): BehaviorSubject<PeriodHierarchyBO> {
        return this._companyChanged
    }

//<editor-fold desc = "Company">
    async addCompany(wbCompanyId: number): Promise<WB_PeriodHierarchyCompanyRelationshipRow> {
        const self = this

        for (let rel of self.drWB_PeriodHierarchy.WB_PeriodHierarchyCompanyRelationshipRow) {
            if (!rel.isActive)
                continue
            if (rel.cif.WB_COMPANY_ID == wbCompanyId)
                return rel
        }

        let cRow: WB_CompanyRow = await self.dbContext.find(esWB_Company, wbCompanyId)
        if (_.isEmpty(cRow))
            return undefined

        let newPeriodHCRRow: WB_PeriodHierarchyCompanyRelationshipRow = <WB_PeriodHierarchyCompanyRelationshipRow>await self.dbContext.createRecordWithKey(esWB_Period_Hierarchy_Company_Relationship)
        newPeriodHCRRow.setDefaults()
        newPeriodHCRRow.cif.WB_PERIODH_ID=self.WB_PeriodHierarchy.cif.WB_PERIODH_ID
        newPeriodHCRRow.cif.WB_COMPANY_ID=wbCompanyId
        newPeriodHCRRow.WB_CompanyRow= cRow
        newPeriodHCRRow.WB_PeriodHierarchyRow = self.WB_PeriodHierarchy

        self.CompanyChanged.next(self)
        return newPeriodHCRRow
    }

    async removeCompany(wbCompanyId: number): Promise<void> {
        const self = this

        for (let rel of self.WB_PeriodHierarchy.WB_PeriodHierarchyCompanyRelationshipRow) {
            if (!rel.isActive)
                continue
            if (rel.cif.WB_PERIODHCR_ID == wbCompanyId) {
                rel.markDeleted()
                break
            }
        }

        self.CompanyChanged.next(self)
    }

    //</editor-fold>

    //<editor-fold desc="IHaveRecordResolver -- static">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const periodh_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, periodh_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Period_Hierarchy, ids)
                .then(result => {
                    resolve();
                })
                .catch(err => {
                    error(err);
                });
        });
    }

    //</editor-fold>

    //<editor-fold desc = "Period Hierarchy Level operations">
    addPeriodHierarchyLevel(options?: any): Promise<PeriodHierarchyBO> {
        const self = this;
        return new Promise<PeriodHierarchyBO>((resolve, reject) => {
            return self.dbContext.createRecordWithKey(esWB_Period_Hierarchy_Level)
                .then((aNewRow: WB_PeriodHierarchyLevelRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_PERIODH_ID = self.WB_PeriodHierarchy.cif.WB_PERIODH_ID;
                    self.dbContext.add(esWB_Period_Hierarchy_Level, aNewRow);
                    resolve(self);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    removePeriodHierarchyLevel(aLevelRowId: number, options?: any): Promise<PeriodHierarchyBO> {
        const self = this;
        return new Promise<PeriodHierarchyBO>((resolve, reject) => {
            _.forEach(self.WB_PeriodHierarchy.WB_PeriodHierarchyLevelRows,(aLevelRow: WB_PeriodHierarchyLevelRow) => {
                if (aLevelRow.isActive && aLevelRow.cif.WB_PERIODH_LEVEL_ID === aLevelRowId) {
                    aLevelRow.markDeleted();
                }
            });
            resolve(self);
        });
    }
    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass( PeriodHierarchyBO , "PeriodHierarchyBO", "Period Hierarchy")